import { _ } from "../../../../core/polyglot/polyglot.js";
import { max, zip } from "@foretagsplatsen/core-helpers/array";
import AbstractForecastReport from "./AbstractForecastReport.js";
import accountRowVisibilityFactory from "../../../reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import CashFlowChartStrategy from "../../../../accounting/forecastEditor/widgets/ForecastReportViewerWidget/CashFlowChartStrategy.js";
import CashFlowForecastFiscalYearCardRenderingStrategy from "../../../../accounting/forecastEditor/widgets/ForecastReportViewerWidget/fiscalYearCardStrategy/CashFlowForecastFiscalYearCardRenderingStrategy.js";
import ClosingBalanceAccountOperation from "../../../keyFigure/accountOperations/ClosingBalanceAccountOperation.js";
import GroupRow from "../../../reports/reports/rows/GroupRow.js";
import PeriodChangeAccountOperation from "../../../keyFigure/accountOperations/PeriodChangeAccountOperation.js";

const liquidFundsId = "CurrentInvestmentsAndLiquidFunds";

/**
 * Report specific to Forecast's Cash flow.
 */
export default class ForecastCashFlow extends AbstractForecastReport {
	getTitle() {
		return _("Cash Flow");
	}

	getSummaryRowTitle() {
		return _("Cash balance");
	}

	isCashFlow() {
		return true;
	}

	getOperationType({ row } = {}) {
		const accountOperation =
			row?.getAccountOperation() || PeriodChangeAccountOperation;

		return accountOperation.getOperationType();
	}

	getKeyFigureOperationType() {
		return ClosingBalanceAccountOperation.getOperationType();
	}

	getChartValues() {
		const barsValues = this.getRows()
			.filter((row) => this._isBarChartRow(row))
			.map((row) => row.getData());

		const lineValues = this._keyFigureValues.get(liquidFundsId);

		const values = new Map();
		values.set("line", lineValues ?? []);
		values.set("bars", barsValues ?? []);

		return values;
	}

	getChartStrategy() {
		return new CashFlowChartStrategy({
			labels: this._getColumnTitles(),
		});
	}

	getChartDataTestName() {
		return "cash-flow-chart";
	}

	getFiscalYearCardRenderingStrategy() {
		return new CashFlowForecastFiscalYearCardRenderingStrategy();
	}

	getValuesForCard() {
		const keyFigureValues = this.getKeyFigureValuesForSummaryRow();
		const zipValuesAndPeriods = zip(keyFigureValues, this.getColumns());
		const indexOfFirstPredictedColumn = this.getColumns().indexOf(
			this.getPredictedColumns()[0],
		);

		const valuesAndPeriodsForConsideredPeriods = zipValuesAndPeriods.slice(
			indexOfFirstPredictedColumn,
		);

		if (valuesAndPeriodsForConsideredPeriods.length === 0) {
			return { lowPointValue: undefined, lowPointPeriod: undefined };
		}

		const zipLowPointValueAndPeriod = max(
			valuesAndPeriodsForConsideredPeriods,
			(zippedKeyFiguresValuesAndColumns) =>
				-zippedKeyFiguresValuesAndColumns[0].getValue(),
		);

		return {
			lowPointValue: zipLowPointValueAndPeriod[0].getValue(),
			lowPointPeriod: zipLowPointValueAndPeriod[1].getPeriod(),
		};
	}

	rulesRendererFor() {
		return () => {};
	}

	_isBarChartRow(row) {
		// We're not using `row.isGroupRow()` as we don't want
		// subclasses of GroupRow.
		return row.getType() === GroupRow.getType();
	}

	static getDefaultAccountVisibility() {
		return accountRowVisibilityFactory.hideAccountsVisibility();
	}

	static getType() {
		return "cash-flow-report";
	}

	static getSummaryRowKeyFigureId() {
		return liquidFundsId;
	}

	static getChartKeyFigureIds() {
		return [liquidFundsId];
	}
}
